import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, X, Sparkles, ExternalLink, Volume2, User as UserIcon, Compass, Send, MessageSquare, FileText, Copy } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TTSPlayButton from "@/components/TTSPlayButton";
import { Button } from "@/components/ui/button";

/**
 * Voice Command Shortcuts — herkent navigatie-intentie in gesproken tekst en navigeert direct
 * (spaart AI-tokens + geeft directe feedback).
 *
 * Format: { patterns: RegExp[], path: string, label: string }
 */
const VOICE_SHORTCUTS = [
  { p: [/open\s+commissie/i, /ga\s+naar\s+commissie/i, /toon\s+commissie/i], path: "/commissie", label: "Commissie" },
  { p: [/open\s+dashboard/i, /ga\s+naar\s+dashboard/i, /naar\s+dashboard/i, /overzicht\s+dashboard/i], path: "/dashboard", label: "Dashboard" },
  { p: [/open\s+werkbon/i, /ga\s+naar\s+werkbon/i, /toon\s+werkbon/i], path: "/werkbonnen", label: "Werkbonnen" },
  { p: [/open\s+planbord/i, /ga\s+naar\s+planbord/i, /toon\s+planning/i], path: "/planbord", label: "Planbord" },
  { p: [/open\s+klant/i, /ga\s+naar\s+klant/i, /toon\s+relaties/i, /open\s+relaties/i], path: "/relaties", label: "Relaties" },
  { p: [/open\s+vaste\s+klant/i, /ga\s+naar\s+vaste\s+klant/i], path: "/vaste-klanten", label: "Vaste klanten" },
  { p: [/open\s+leads/i, /ga\s+naar\s+leads/i, /toon\s+leads/i], path: "/leads", label: "Leads" },
  { p: [/open\s+offerte/i, /ga\s+naar\s+offerte/i, /toon\s+offertes/i], path: "/offertes", label: "Offertes" },
  { p: [/open\s+facturen/i, /ga\s+naar\s+facturen/i, /toon\s+facturen/i], path: "/facturen", label: "Facturen" },
  { p: [/open\s+cash\s*flow/i, /ga\s+naar\s+cash/i], path: "/cash-flow", label: "Cash flow" },
  { p: [/open\s+ai(\s+assistent)?/i, /ga\s+naar\s+ai/i, /toon\s+ai/i], path: "/ai-assistent", label: "AI Assistent" },
  { p: [/open\s+ticket/i, /ga\s+naar\s+ticket/i, /toon\s+tickets/i], path: "/tickets", label: "Tickets" },
  { p: [/open\s+taken/i, /ga\s+naar\s+taken/i, /toon\s+taken/i], path: "/taken", label: "Taken" },
  { p: [/open\s+verlof/i, /ga\s+naar\s+verlof/i, /toon\s+verlof/i], path: "/verlof", label: "Verlof" },
  { p: [/open\s+medewerker/i, /ga\s+naar\s+medewerker/i, /toon\s+medewerkers/i], path: "/medewerkers", label: "Medewerkers" },
  { p: [/open\s+bekwaamheden/i, /ga\s+naar\s+bekwaamheden/i, /toon\s+certificaten/i], path: "/bekwaamheden", label: "Bekwaamheden" },
  { p: [/open\s+bestelling/i, /ga\s+naar\s+bestelling/i], path: "/bestellingen", label: "Bestellingen" },
  { p: [/open\s+voorraad/i, /ga\s+naar\s+voorraad/i], path: "/voorraad-teller", label: "Voorraad" },
  { p: [/open\s+locatie/i, /toon\s+locatie/i, /waar\s+is\s+het\s+team/i, /live\s+locatie/i], path: "/live-locatie", label: "Live locatie" },
  { p: [/open\s+kaart/i, /toon\s+kaart/i, /route\s+kaart/i], path: "/locatie-kaart", label: "Locatie-kaart" },
  { p: [/open\s+mail/i, /ga\s+naar\s+mail/i, /toon\s+mail/i], path: "/mail-dashboard", label: "Mail" },
  { p: [/open\s+agenda/i, /ga\s+naar\s+agenda/i, /toon\s+vergader/i], path: "/agenda", label: "Agenda" },
  { p: [/open\s+social(\s+media)?/i, /ga\s+naar\s+social/i], path: "/social-media", label: "Social media" },
  { p: [/(sluit( het)?( paneel)?|verberg( het)?( paneel)?|ga terug)[.!]?$/i], action: "close" },
];

function detectShortcut(text) {
  const t = (text || "").trim();
  if (!t) return null;
  for (const sc of VOICE_SHORTCUTS) {
    for (const rx of sc.p) {
      // Anker op het begin: alleen echte commando's, geen vragen die het keyword bevatten
      const anchored = new RegExp("^\\s*(?:" + rx.source + ")", "i");
      if (anchored.test(t)) return sc;
    }
  }
  return null;
}

/**
 * FloatingVoiceAssistant — Overal-beschikbare mic-knop rechtsonder.
 *
 * UX-flow (tap-to-toggle):
 *   1. Klik op mic-knop  →  panel opent
 *   2. Klik op grote mic-knop in panel → opname start (rood pulserend)
 *   3. Klik opnieuw → opname stopt → transcribe via /ai/voice/transcribe
 *   4. Transcript wordt automatisch naar /ai-agent/chat gestuurd
 *   5. Antwoord verschijnt in-panel, met "Voorlees" knop
 *
 * Voorbeelden: "hoeveel werkbonnen deze week", "maak taak morgen bel Fabevo", etc.
 *
 * De sessie is uniek per pagina-load; via "Open volledige chat" wordt de
 * gebruiker naar /ai-assistent gestuurd met de zichtbare geschiedenis.
 */
export default function FloatingVoiceAssistant() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [supported, setSupported] = useState(true);
  const [messages, setMessages] = useState([]); // [{rol, content, id, acties}]
  const [sessionId] = useState(() => "voice-" + Date.now());
  const [mode, setMode] = useState("chat"); // chat | tekst
  const [typed, setTyped] = useState("");
  const [bewerkTekst, setBewerkTekst] = useState("");
  const [bewerkResultaat, setBewerkResultaat] = useState("");
  const [bewerkBusy, setBewerkBusy] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const scrollRef = useRef(null);

  // Verberg op login/reset-pagina's + op /commissie (want daar heeft Mikail eigen voice-input in het paneel)
  const HIDDEN_PATHS = ["/login", "/wachtwoord-vergeten", "/wachtwoord-reset", "/wachtwoord-instellen", "/onboarding-invite", "/commissie"];
  const shouldHide = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p)) || !user;

  useEffect(() => {
    if (typeof window === "undefined" || !window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
      setSupported(false);
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  // ESC om te sluiten
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!supported) {
      toast.error("Audio-opname niet ondersteund in deze browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      let mime = "";
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
      for (const m of candidates) {
        if (window.MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (blob.size < 1000) {
          toast.warning("Opname te kort — probeer opnieuw");
          return;
        }
        await handleAudio(blob, mr.mimeType || "audio/webm");
      };
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error("Microfoon-toegang geweigerd of niet beschikbaar");
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const handleAudio = async (blob, mime) => {
    setTranscribing(true);
    let transcript = "";
    try {
      const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm";
      const fd = new FormData();
      fd.append("file", blob, `spraak.${ext}`);
      fd.append("language", "nl");
      const { data } = await api.post("/ai/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      transcript = (data?.text || "").trim();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Transcriptie mislukt");
    }
    setTranscribing(false);
    if (!transcript) {
      toast.warning("Geen tekst herkend — probeer opnieuw");
      return;
    }
    if (mode === "tekst") {
      setBewerkTekst((t) => (t ? t + "\n" + transcript : transcript));
      return;
    }
    await handleText(transcript);
  };

  const handleText = async (transcript) => {
    // Check op navigatie-shortcut vóórdat we naar AI sturen
    const sc = detectShortcut(transcript);
    if (sc) {
      const userMsg = { rol: "user", content: transcript, id: "u-" + Date.now() };
      if (sc.action === "close") {
        setMessages((m) => [...m, userMsg, { rol: "assistant", content: "Paneel gesloten.", id: "a-close-" + Date.now() }]);
        setTimeout(() => setOpen(false), 400);
        return;
      }
      setMessages((m) => [...m, userMsg, {
        rol: "assistant",
        content: `Ik open **${sc.label}** voor je…`,
        id: "a-nav-" + Date.now(),
      }]);
      toast.success(`→ ${sc.label}`);
      setTimeout(() => {
        navigate(sc.path);
        setOpen(false);
      }, 500);
      return;
    }
    // Config-command detectie ("zet/wijzig/verander [X]")
    const isCfgCmd = /^(zet|wijzig|verander|pas\s+aan|verhoog|verlaag|update)\s+/i.test(transcript.trim());
    if (isCfgCmd) {
      await sendConfigCommand(transcript);
      return;
    }
    await sendToAgent(transcript);
  };

  const sendConfigCommand = async (msg) => {
    const userMsg = { rol: "user", content: msg, id: "u-" + Date.now() };
    setMessages((m) => [...m, userMsg]);
    setThinking(true);
    try {
      const { data } = await api.post("/commissie/ai-config-command", { message: msg });
      const label = data.intent === "update" ? "🛠️ Wijziging voorgesteld"
                  : data.intent === "question" ? "❓ Verduidelijking nodig"
                  : "💬";
      setMessages((m) => [...m, {
        rol: "assistant",
        content: `${label}\n\n${data.reply || ""}`,
        pendingChanges: data.intent === "update" ? (data.changes || []) : null,
        id: "a-cfg-" + Date.now(),
      }]);
      if (data.intent === "update" && (data.changes || []).length > 0) {
        toast.info("Wijziging voorgesteld — tik 'Doorvoeren' in het paneel");
      }
    } catch (err) {
      const msg403 = err?.response?.status === 403;
      toast.error(msg403 ? "Config-wijzigingen alleen voor Mikail" : (formatErr(err.response?.data?.detail) || "AI fout"));
      setMessages((m) => [...m, { rol: "assistant", content: msg403 ? "⚠️ Deze functie is alleen voor Mikail." : "⚠️ Er ging iets mis.", id: "err-" + Date.now() }]);
    }
    setThinking(false);
  };

  const applyPendingChanges = async (changes, msgId) => {
    setThinking(true);
    try {
      const { data } = await api.post("/commissie/config-apply", { changes });
      const ok = data.uitgevoerd?.length || 0;
      toast.success(`${ok} wijziging(en) doorgevoerd`);
      setMessages((m) => m.map((x) => x.id === msgId ? { ...x, pendingChanges: null, applied: true } : x)
                       .concat([{ rol: "assistant", content: `✅ **Doorgevoerd:** ${ok} wijziging(en)`, id: "applied-" + Date.now() }]));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Doorvoeren mislukt");
    }
    setThinking(false);
  };

  const sendToAgent = async (msg) => {
    if (!msg || thinking) return;
    const userMsg = { rol: "user", content: msg, id: "u-" + Date.now() };
    setMessages((m) => [...m, userMsg]);
    setThinking(true);
    try {
      const { data } = await api.post("/ai-agent/chat", { session_id: sessionId, message: msg, fast: true });
      setMessages((m) => [...m, {
        rol: "assistant",
        content: data.reply,
        acties: data.acties_uitgevoerd || [],
        id: "a-" + Date.now(),
      }]);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "AI fout");
      setMessages((m) => [...m, { rol: "assistant", content: "Er ging iets mis. Probeer het opnieuw.", id: "a-err-" + Date.now() }]);
    }
    setThinking(false);
  };

  const submitTyped = async () => {
    const t = typed.trim();
    if (!t || transcribing || thinking) return;
    setTyped("");
    await handleText(t);
  };

  const verwerkTekst = async (actie) => {
    if (!bewerkTekst.trim() || bewerkBusy) return;
    setBewerkBusy(true);
    try {
      const { data } = await api.post("/ai/tekst-verwerken", { tekst: bewerkTekst, actie });
      setBewerkResultaat(data.resultaat || "");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "AI fout");
    }
    setBewerkBusy(false);
  };

  const kopieerResultaat = async () => {
    try {
      await navigator.clipboard.writeText(bewerkResultaat);
      toast.success("Gekopieerd naar klembord");
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  const handleMicClick = () => {
    if (transcribing || thinking) return;
    if (recording) stopRecording();
    else startRecording();
  };

  const openFull = () => {
    setOpen(false);
    navigate("/ai-assistent");
  };

  if (shouldHide) return null;

  const busy = transcribing || thinking;

  return (
    <>
      {/* FAB — Floating Action Button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed z-[45] bottom-[calc(env(safe-area-inset-bottom)+76px)] right-4 md:bottom-6 md:right-6 h-14 w-14 rounded-full bg-[hsl(var(--brand-blue))] text-white shadow-lg hover:bg-[hsl(var(--brand-blue))]/90 flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
          title="AI Voice-assistent (klik om te spreken)"
          aria-label="AI Voice-assistent openen"
          data-testid="floating-voice-fab"
        >
          <Mic className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed z-[46] inset-x-0 bottom-0 md:inset-auto md:bottom-6 md:right-6 md:w-[400px] md:max-h-[600px] bg-white border border-slate-200 shadow-2xl md:rounded-lg flex flex-col overflow-hidden"
          style={{ maxHeight: "calc(100vh - 40px)" }}
          data-testid="floating-voice-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-[hsl(var(--brand-blue))] flex items-center justify-center text-white">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-800">Voice AI</div>
                <div className="text-[10px] text-slate-500 leading-none">Spreek of typ je vraag</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={openFull}
                title="Open volledige AI-chat"
                className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                data-testid="floating-voice-open-full"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                title="Sluiten"
                className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                data-testid="floating-voice-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Mode-tabs */}
          <div className="flex border-b border-slate-200 bg-white text-xs">
            <button type="button" onClick={() => setMode("chat")}
              className={`flex-1 flex items-center justify-center gap-1 py-2 font-medium transition-colors ${mode === "chat" ? "text-[hsl(var(--brand-blue))] border-b-2 border-[hsl(var(--brand-blue))]" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="voice-mode-chat">
              <MessageSquare className="h-3.5 w-3.5" /> Assistent
            </button>
            <button type="button" onClick={() => setMode("tekst")}
              className={`flex-1 flex items-center justify-center gap-1 py-2 font-medium transition-colors ${mode === "tekst" ? "text-[hsl(var(--brand-blue))] border-b-2 border-[hsl(var(--brand-blue))]" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="voice-mode-tekst">
              <FileText className="h-3.5 w-3.5" /> Tekst verwerken
            </button>
          </div>

          {/* Tekst verwerken */}
          {mode === "tekst" && (
            <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[220px] max-h-[380px]">
              <textarea value={bewerkTekst} onChange={(e) => setBewerkTekst(e.target.value)} rows={5}
                placeholder="Plak of typ hier je tekst — of dicteer via de mic-knop hieronder…"
                className="w-full border border-slate-300 rounded-md p-2 text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--brand-blue))]"
                data-testid="voice-tekst-input" />
              <div className="flex flex-wrap gap-1">
                {[["netter", "Netter maken"], ["samenvatten", "Samenvatten"], ["zakelijk", "Zakelijk (u-vorm)"], ["punten", "Bulletpoints"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => verwerkTekst(k)} disabled={bewerkBusy || !bewerkTekst.trim()}
                    className="px-2 py-1 rounded border border-slate-300 bg-white text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    data-testid={`voice-tekst-${k}`}>
                    {l}
                  </button>
                ))}
              </div>
              {bewerkBusy && (
                <div className="flex gap-2 items-center text-xs text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> AI verwerkt…
                </div>
              )}
              {bewerkResultaat && !bewerkBusy && (
                <div className="border border-slate-200 rounded-md p-2 bg-slate-50 text-xs text-slate-800 whitespace-pre-wrap" data-testid="voice-tekst-resultaat">
                  {bewerkResultaat}
                  <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-200">
                    <button type="button" onClick={kopieerResultaat}
                      className="flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white text-[10px] font-medium hover:bg-slate-100"
                      data-testid="voice-tekst-kopieer">
                      <Copy className="h-3 w-3" /> Kopieer
                    </button>
                    <button type="button" onClick={() => { setBewerkTekst(bewerkResultaat); setBewerkResultaat(""); }}
                      className="px-2 py-1 rounded border border-slate-300 bg-white text-[10px] font-medium hover:bg-slate-100"
                      data-testid="voice-tekst-hergebruik">
                      Verder bewerken
                    </button>
                    <TTSPlayButton text={bewerkResultaat} testId="voice-tekst-tts" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Berichten */}
          <div ref={scrollRef} className={`flex-1 overflow-y-auto p-3 space-y-3 min-h-[220px] max-h-[380px] ${mode !== "chat" ? "hidden" : ""}`}>
            {messages.length === 0 && !busy && (
              <div className="text-center py-6 text-slate-500 text-xs">
                <Mic className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <p className="font-medium text-slate-700 mb-1">Tik op de mic of typ je vraag hieronder</p>
                <p className="mt-2 text-slate-600 flex items-center justify-center gap-1"><Compass className="h-3 w-3" /> Snelle navigatie:</p>
                <ul className="mt-1 space-y-0.5 text-slate-400">
                  <li>"Open commissie"</li>
                  <li>"Ga naar dashboard"</li>
                  <li>"Toon werkbonnen"</li>
                </ul>
                <p className="mt-2 text-slate-600">Of stel een vraag:</p>
                <ul className="mt-1 space-y-0.5 text-slate-400">
                  <li>"Hoeveel werkbonnen deze week"</li>
                  <li>"Maak taak morgen: bel Fabevo"</li>
                </ul>
                <p className="mt-2 text-slate-600">Of geef een commando:</p>
                <ul className="mt-1 space-y-0.5 text-slate-400">
                  <li>"Zet Ruben op €15"</li>
                  <li>"Wijzig IMA minimum naar €1250"</li>
                </ul>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-2 ${m.rol === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center ${m.rol === "user" ? "bg-slate-200 text-slate-700" : "bg-[hsl(var(--brand-blue))] text-white"}`}>
                  {m.rol === "user" ? <UserIcon className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                </div>
                <div className={`max-w-[85%] space-y-1 ${m.rol === "user" ? "text-right" : ""}`}>
                  <div className={`px-3 py-2 rounded-lg text-xs leading-relaxed whitespace-pre-wrap ${
                    m.rol === "user" ? "bg-[hsl(var(--brand-blue))] text-white" : "bg-slate-100 text-slate-800"
                  }`}>
                    {m.content}
                  </div>
                  {m.rol === "assistant" && m.content && (
                    <div className="flex items-center gap-1">
                      <TTSPlayButton text={m.content} testId={`floating-voice-tts-${m.id}`} />
                    </div>
                  )}
                  {m.rol === "assistant" && m.pendingChanges?.length > 0 && !m.applied && (
                    <div className="border border-amber-300 bg-amber-50 rounded p-2 space-y-1.5 text-[10px]">
                      {m.pendingChanges.map((c, i) => (
                        <div key={i} className="bg-white border border-amber-200 rounded p-1.5">
                          <div className="font-medium text-slate-800">
                            {c.action === "update_medewerker" ? `${c.target_key} · ${c.field}` : c.target_key}
                          </div>
                          <div className="text-slate-600">
                            {c.old_value != null && <span className="line-through text-slate-400 mr-1">{JSON.stringify(c.old_value)}</span>}
                            <span className="text-emerald-700">→ {JSON.stringify(c.new_value)}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex gap-1 pt-1">
                        <button type="button" onClick={() => applyPendingChanges(m.pendingChanges, m.id)}
                          className="flex-1 px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-medium"
                          data-testid="voice-apply-changes">
                          ✓ Doorvoeren
                        </button>
                        <button type="button" onClick={() => setMessages((s) => s.map((x) => x.id === m.id ? { ...x, pendingChanges: null } : x))}
                          className="px-2 py-1 rounded bg-white border border-slate-300 text-slate-700 text-[10px]">
                          Annuleren
                        </button>
                      </div>
                    </div>
                  )}
                  {m.rol === "assistant" && m.acties?.length > 0 && (
                    <div className="text-[10px] text-emerald-700">
                      ✓ {m.acties.length} actie{m.acties.length > 1 ? "s" : ""} uitgevoerd
                    </div>
                  )}
                </div>
              </div>
            ))}
            {transcribing && (
              <div className="flex gap-2 items-center text-xs text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Transcriberen…
              </div>
            )}
            {thinking && (
              <div className="flex gap-2 items-center text-xs text-slate-500">
                <Sparkles className="h-4 w-4 animate-pulse text-[hsl(var(--brand-blue))]" /> AI denkt na…
              </div>
            )}
          </div>

          {/* Mic-knop */}
          <div className="border-t border-slate-200 p-4 bg-white">
            {mode === "chat" && (
              <div className="flex gap-2 mb-3">
                <input value={typed} onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submitTyped(); }}
                  placeholder="Of typ je vraag…" disabled={busy}
                  className="flex-1 border border-slate-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[hsl(var(--brand-blue))]"
                  data-testid="voice-typed-input" />
                <button type="button" onClick={submitTyped} disabled={busy || !typed.trim()}
                  className="px-3 rounded-md bg-[hsl(var(--brand-blue))] text-white disabled:opacity-40 hover:opacity-90"
                  data-testid="voice-typed-send" title="Versturen">
                  <Send className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleMicClick}
                disabled={busy || !supported}
                className={`h-16 w-16 rounded-full flex items-center justify-center shadow-md transition-all ${
                  recording
                    ? "bg-red-500 text-white animate-pulse scale-110"
                    : busy
                    ? "bg-slate-200 text-slate-400"
                    : "bg-[hsl(var(--brand-blue))] text-white hover:scale-105"
                }`}
                title={recording ? "Stop opname" : "Start opname"}
                aria-label={recording ? "Stop opname" : "Start opname"}
                data-testid="floating-voice-mic"
              >
                {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : recording ? <Square className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
              </button>
              <div className="text-[11px] text-slate-500 text-center">
                {recording ? "Tik nogmaals om te stoppen" : busy ? "Even geduld…" : mode === "tekst" ? "Tik om te dicteren (vult het tekstveld)" : "Tik om te spreken"}
              </div>
            </div>
            {!supported && (
              <div className="mt-2 text-[10px] text-red-600 text-center">Browser ondersteunt geen audio-opname.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
